import { AdminSyncNotificationListTemplate } from '@/components/templates';
import {
  useAdminSyncNotificationCount,
  useAdminSyncNotificationList,
  useMarkReadSyncNotificationMutation,
  useRetrySyncMutation,
  useRetrySyncRecordMutation,
} from '@/hooks/admin';
import { downloadDriftCsv } from '@/services/admin/sync-notifications';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import useAppModal from '@/hooks/useAppModal';
import {
  SYNC_NOTIFICATION_CATEGORY,
  SyncNotificationCategory,
} from '@/types/admin-sync-notification';
import { useSearchParams, useParams } from 'react-router-dom';
import { useState } from 'react';

const PER_PAGE = 20;

const SyncNotificationListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const appModal = useAppModal();
  /** Notification id whose 明細CSV is in flight — drives the per-row spinner. */
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const page = Number(searchParams.get('page') || '1');
  const unreadOnly = searchParams.get('unreadOnly') === '1';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const category = (searchParams.get('category') ||
    SYNC_NOTIFICATION_CATEGORY.SYNC_FAILED) as SyncNotificationCategory;

  const breadcrumbItems = [
    {
      title: '同期通知',
    },
  ];

  const {
    data,
    isLoading,
    isError: listError,
  } = useAdminSyncNotificationList({
    page,
    perPage: PER_PAGE,
    // No syncState: BD §2 検索条件 has three conditions (tab / 受信日 / 未読のみ) and 同期状態 is not one of
    // them — it is a column (BD 13.0). The API still accepts the parameter if BD ever adds the control.
    unreadOnly: unreadOnly || undefined,
    category,
    from: from || undefined,
    to: to || undefined,
  });

  // Same query the header bell polls – cached, so the tab badges cost this screen no extra request.
  const { data: unread } = useAdminSyncNotificationCount();

  const markReadMutation = useMarkReadSyncNotificationMutation();
  const retryRecordMutation = useRetrySyncRecordMutation();
  const retryAllMutation = useRetrySyncMutation();

  const listData = data?.data || [];
  const isBusy =
    markReadMutation.isPending || retryRecordMutation.isPending || retryAllMutation.isPending;

  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: listData.length,
  });

  const handlePageChange = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', nextPage.toString());
    setSearchParams(params);
  };

  const handleCategoryChange = (value: SyncNotificationCategory) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', value);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleDateRangeChange = (nextFrom: string, nextTo: string) => {
    const params = new URLSearchParams(searchParams);
    if (nextFrom) params.set('from', nextFrom);
    else params.delete('from');
    if (nextTo) params.set('to', nextTo);
    else params.delete('to');
    params.set('page', '1'); // a narrower range can leave the current page past the end of the list
    setSearchParams(params);
  };

  const handleUnreadOnlyChange = (value: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('unreadOnly', '1');
    else params.delete('unreadOnly');
    params.set('page', '1');
    setSearchParams(params);
  };

  // BD 20.0 — 再同期 and 全て再同期 both overwrite what Shinsa currently holds, including anything a reviewer
  // typed there by hand. That is not recoverable from this screen, so it is asked before it happens rather
  // than reported after.
  const confirmResync = (onOk: () => void) => {
    appModal.confirm({
      content: (
        <>
          <p>
            このレセプトを審査システムに再送信します。現在の内容で上書きされます。よろしいですか？
          </p>
          <p className="m-0 text-sm text-gray-500">
            審査側で手入力された内容は上書きされますが、審査結果・返戻・入金の情報は保持されます。
          </p>
        </>
      ),
      okText: 'はい',
      cancelText: 'いいえ',
      onOk,
    });
  };

  // Per-row: re-push only this record.
  const handleRetry = (aggregate: string, aggregateId: string) => {
    confirmResync(() =>
      retryRecordMutation.mutate({ siteType: currentSite, aggregate, aggregateId })
    );
  };

  // BD 17.0 — ONE call for the whole inbox. It used to loop over the distinct aggregates of the CURRENT PAGE,
  // so a backlog whose rezept-r2 rows all sat on page 3 was skipped by a button labelled 「全て」. The server
  // resolves the set now, because it is the only side that can see past the page.
  const handleSyncAll = () => {
    confirmResync(() => retryAllMutation.mutate({ siteType: currentSite, category }));
  };

  const handleMarkRead = (id: number) => {
    markReadMutation.mutate({ siteType: currentSite, id });
  };

  /**
   * 明細CSV — a plain GET, not a mutation: nothing on the server changes and nothing in the cache goes stale,
   * so react-query would only be holding a result nobody reads.
   *
   * The failure that matters is 404 — the nightly run could not write the file, or it has been cleaned up.
   * The button is only drawn where the row says a file exists, so a 404 here means the file went away since
   * the page loaded; saying so is more useful than a generic 「失敗しました」.
   */
  const handleDownloadCsv = async (id: number) => {
    setDownloadingId(id);
    try {
      await downloadDriftCsv(currentSite, id);
    } catch {
      appModal.error({ content: '明細ファイルを取得できませんでした。次回の照合後に再度お試しください。' });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <AdminSyncNotificationListTemplate
      data={listData}
      total={data?.meta.total || 0}
      current={data?.meta.current_page || 1}
      pageSize={data?.meta.per_page || PER_PAGE}
      onChange={handlePageChange}
      onRetry={handleRetry}
      onSyncAll={handleSyncAll}
      onMarkRead={handleMarkRead}
      onDownloadCsv={handleDownloadCsv}
      downloadingId={downloadingId}
      unreadOnly={unreadOnly}
      onUnreadOnlyChange={handleUnreadOnlyChange}
      from={from}
      to={to}
      onDateRangeChange={handleDateRangeChange}
      category={category}
      onCategoryChange={handleCategoryChange}
      unreadCounts={unread?.byCategory}
      // BD 17.0 — 「Enable khi có ít nhất 1 record cần đồng bộ lại」. Undefined while the count is in flight,
      // which the template reads as 「not yet」 rather than 「none」.
      resyncPending={unread?.resyncPending?.[category]}
      isBusy={isBusy}
      isSyncingAll={retryAllMutation.isPending}
      retryingAggregateId={
        retryRecordMutation.isPending ? (retryRecordMutation.variables?.aggregateId ?? null) : null
      }
      readingId={markReadMutation.isPending ? (markReadMutation.variables?.id ?? null) : null}
      breadcrumbItems={breadcrumbItems}
      isLoading={isLoading}
      // BD 10.0 — the server decides, because it knows the admin's tenant family and this screen only knows
      // the URL. Defaults to hidden while the first request is in flight, so the column does not appear and
      // then vanish on a single-site portal.
      showSiteColumn={data?.site_scope?.shows_site_column ?? false}
    />
  );
};

export default SyncNotificationListPage;
